<template>
  <nav
    class="flex w-full items-end justify-between border-neutral-200"
    role="navigation"
    aria-label="pagination"
  >
    <SfButton
      size="lg"
      aria-label="Go to previous page"
      :disabled="selectedPage <= 1"
      variant="tertiary"
      class="gap-3 !px-3 sm:px-6"
      @click="prev"
    >
      <template #prefix>
        <SfIconChevronLeft />
      </template>
      <span class="hidden desktop:inline-flex"> {{ $t('Previous') }} </span>
    </SfButton>
    <ul class="flex justify-center">
      <li v-if="!pages.includes(1)">
        <div
          :class="[
            'flex border-t-4 border-transparent pt-1',
            { 'border-t-4 !border-primary-500 font-medium': selectedPage === 1 },
          ]"
        >
          <button
            type="button"
            class="min-w-[38px] rounded-md p-3 text-neutral-500 hover:bg-primary-100 hover:text-primary-800 active:bg-primary-200 active:text-primary-900 sm:px-4 md:w-12"
            :aria-current="selectedPage === 1"
            @click="setPage(1)"
          >
            1
          </button>
        </div>
      </li>
      <li v-if="startPage > 2">
        <div class="flex border-t-4 border-transparent pt-1">
          <button
            type="button"
            disabled
            aria-hidden="true"
            class="rounded-md px-4 py-3 text-neutral-500 md:w-12"
          >
            ...
          </button>
        </div>
      </li>
      <li v-if="maxVisiblePages === 1 && selectedPage === totalPages">
        <div class="flex border-t-4 border-transparent pt-1">
          <button
            type="button"
            class="min-w-[38px] rounded-md p-3 text-neutral-500 hover:bg-primary-100 hover:text-primary-800 active:bg-primary-200 active:text-primary-900 sm:px-4 md:w-12"
            :aria-current="endPage - 1 === selectedPage"
            @click="setPage(endPage - 1)"
          >
            {{ endPage - 1 }}
          </button>
        </div>
      </li>
      <li
        v-for="page in pages"
        :key="`page-${page}`"
      >
        <div class="flex pt-2">
          <button
            type="button"
            :class="[
              'min-w-[38px] rounded-md p-3 text-neutral-500 hover:bg-primary-100 hover:text-primary-800 active:bg-primary-200 active:text-primary-900 sm:px-4 md:w-12',
              { 'bg-primary-50 !font-bold !text-primary-800 hover:!text-primary-900 active:!text-primary-900': selectedPage === page },
            ]"
            :aria-label="`Page ${page} of ${totalPages}`"
            :aria-current="selectedPage === page"
            @click="setPage(page)"
          >
            {{ page }}
          </button>
        </div>
      </li>
      <li v-if="maxVisiblePages === 1 && selectedPage === 1">
        <div class="flex border-t-4 border-transparent pt-2">
          <button
            type="button"
            class="min-w-[38px] rounded-md p-3 text-neutral-500 hover:bg-primary-100 hover:text-primary-800 active:bg-primary-200 active:text-primary-900 sm:px-4 md:w-12"
            :aria-label="`Page 2 of ${totalPages}`"
            @click="setPage(2)"
          >
            2
          </button>
        </div>
      </li>
      <li v-if="endPage < totalPages - 1">
        <div class="flex border-t-4 border-transparent pt-1">
          <button
            type="button"
            disabled
            aria-hidden="true"
            class="rounded-md px-4 py-3 text-neutral-500 md:w-12"
          >
            ...
          </button>
        </div>
      </li>
      <li v-if="!pages.includes(totalPages)">
        <div
          :class="[
            'flex border-t-4 border-transparent pt-1',
            { 'border-t-4 !border-primary-500 font-medium': selectedPage === totalPages },
          ]"
        >
          <button
            type="button"
            class="min-w-[38px] rounded-md p-3 text-neutral-500 hover:bg-primary-100 hover:text-primary-800 active:bg-primary-200 active:text-primary-900 sm:px-4 md:w-12"
            :aria-current="totalPages === selectedPage"
            @click="setPage(totalPages)"
          >
            {{ totalPages }}
          </button>
        </div>
      </li>
    </ul>
    <SfButton
      size="lg"
      aria-label="Go to next page"
      :disabled="selectedPage >= totalPages"
      variant="tertiary"
      class="gap-3 !px-3 sm:px-6"
      @click="next"
    >
      <span class="hidden desktop:inline-flex"> {{ $t('Next') }} </span>
      <template #suffix>
        <SfIconChevronRight />
      </template>
    </SfButton>
  </nav>
</template>

<script lang="ts" setup>
import { SfButton, SfIconChevronLeft, SfIconChevronRight, usePagination } from '@storefront-ui/vue';

const props = defineProps<{
  total: number;
  current?: number;
  limit?: number;
}>();

const { $config } = useNuxtApp();
const { isDesktop } = useDevice();
const total = toRef(props.total);
const current = toRef(props.current);
const moreProducts = ref(false);
const navigated = ref(false);

const emit = defineEmits(['click:navigate']);

const { totalPages, pages, selectedPage, startPage, endPage, next, prev, setPage, maxVisiblePages } = usePagination({
  totalItems: total.value,
  currentPage: current.value,
  pageSize: props.limit ? props.limit : (isDesktop ? $config.public.category?.itemsPerPage?.desktop : $config.public.category?.itemsPerPage?.mobile),
  maxPages: isDesktop ? 5 : 3,
});

watch(selectedPage, (value) => {
  if (moreProducts.value) {
    moreProducts.value = false;
    return;
  }

  navigated.value = true;
  emit('click:navigate', value);
});

watch(() => props.current, (newValue, oldValue) => {
  if (!oldValue || navigated.value) {
    navigated.value = false;
    return;
  }

  moreProducts.value = true;
  next();
});
</script>
